import { useTranslation } from "react-i18next";
import type { DownloadFile } from "../../content/downloads";
import { PlatformIcon } from "./PlatformIcon";
import { DownloadButton } from "./DownloadButton";

interface PlatformCardProps {
  platform: {
    id: string;
    name: string;
    description: string;
    icon: "windows" | "apple" | "linux";
    version?: string;
    releaseDate?: string;
    files: DownloadFile[];
  };
  isHighlighted?: boolean;
  /** When set, the file matching this architecture is promoted to primary style */
  preferredArch?: "arm64" | "x64" | "unknown";
}

function formatBuildDate(isoString: string): string {
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZoneName: "short",
    }).format(date);
  } catch {
    return "";
  }
}

/**
 * Sort files so the one matching the preferred architecture comes first.
 * Matches by checking sublabel/filename for architecture keywords.
 */
function sortByArch(files: DownloadFile[], arch?: string): DownloadFile[] {
  if (!arch || arch === "unknown" || files.length <= 1) return files;

  const archKeywords =
    arch === "arm64" ? ["arm64", "aarch64", "apple silicon"] : ["x64", "x86_64", "intel"];

  const sorted = [...files].sort((a, b) => {
    const aMatch = archKeywords.some(
      (kw) =>
        a.sublabel?.toLowerCase().includes(kw) ||
        a.filename.toLowerCase().includes(kw) ||
        a.label?.toLowerCase().includes(kw)
    );
    const bMatch = archKeywords.some(
      (kw) =>
        b.sublabel?.toLowerCase().includes(kw) ||
        b.filename.toLowerCase().includes(kw) ||
        b.label?.toLowerCase().includes(kw)
    );

    if (aMatch && !bMatch) return -1;
    if (!aMatch && bMatch) return 1;
    return 0;
  });

  return sorted;
}

export function PlatformCard({ platform, isHighlighted, preferredArch }: PlatformCardProps) {
  const { t } = useTranslation();

  return (
    <div
      className={`relative p-6 rounded-xl border transition-all ${
        isHighlighted
          ? "bg-dark-card/80 border-blue-500/50 ring-1 ring-blue-500/20"
          : "bg-dark-card border-dark-border"
      }`}
    >
      {isHighlighted && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-xs font-medium px-3 py-1 rounded-full">
          {t('downloads.platformCard.recommended')}
        </div>
      )}

      <div className="flex items-center gap-4 mb-4">
        <div className="p-3 rounded-lg bg-dark-bg text-warm-400">
          <PlatformIcon platform={platform.icon} className="w-8 h-8" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-xl font-semibold text-warm-50">{platform.name}</h3>
            {platform.version && (
              <span className="text-xs text-warm-500 bg-dark-bg px-2 py-0.5 rounded">
                v{platform.version}
              </span>
            )}
          </div>
          <p className="text-sm text-warm-500">
            {platform.description}
            {platform.releaseDate && platform.version && (
              <>
                {" · "}
                <span className="text-warm-600">{formatBuildDate(platform.releaseDate)}</span>
              </>
            )}
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {platform.files.length === 0 ? (
          <p className="text-sm text-warm-500 italic py-2">{t('downloads.platformCard.noBuilds')}</p>
        ) : (
          sortByArch(
            platform.files,
            preferredArch ?? (platform.icon === "apple" ? "arm64" : undefined)
          ).map((file, index) => (
            <DownloadButton
              key={file.filename}
              file={file}
              variant={isHighlighted && index === 0 ? "primary" : "secondary"}
            />
          ))
        )}
      </div>
    </div>
  );
}
